import React, { useState, useEffect, useCallback } from 'react';
import MaskedDatePicker from './MaskedDatePicker';
import { formatDateForInput } from '../utils/dateUtils';
import {
  Building2,
  CreditCard,
  ShoppingCart,
  Wallet,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Search,
  AlertCircle,
  BarChart2
} from 'lucide-react';

const API_BASE = 'https://apisaleeasy.psisat.com/api/corp-erp';

const fmt = (n) => Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('th-TH', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';

const firstOfMonth = formatDateForInput(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
const todayIso = formatDateForInput(new Date());

export default function CorpErpComponent() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [suppliers, setSuppliers] = useState([]);

  // AP state
  const [apData, setApData] = useState(null);
  const [apFilters, setApFilters] = useState({ start_date: firstOfMonth, end_date: todayIso, supp_number: '', page: 1, limit: 50 });
  const [apLoading, setApLoading] = useState(false);
  const [apError, setApError] = useState('');

  // PO state
  const [poData, setPoData] = useState(null);
  const [poFilters, setPoFilters] = useState({ start_date: firstOfMonth, end_date: todayIso, supp_number: '', open_close: '', page: 1, limit: 50 });
  const [poLoading, setPoLoading] = useState(false);
  const [poError, setPoError] = useState('');

  // PettyCash state
  const [pettyData, setPettyData] = useState(null);
  const [pettyFilters, setPettyFilters] = useState({ start_date: firstOfMonth, end_date: todayIso, gexpend_code: '', dept_code: '', page: 1, limit: 50 });
  const [pettyLoading, setPettyLoading] = useState(false);
  const [pettyError, setPettyError] = useState('');

  // Monthly expenses state
  const [monthlyData, setMonthlyData] = useState(null);
  const [monthlyYear, setMonthlyYear] = useState(new Date().getFullYear());
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [monthlyError, setMonthlyError] = useState('');

  // Load dashboard + suppliers on mount
  useEffect(() => {
    fetch(`${API_BASE}/dashboard`)
      .then(r => r.json())
      .then(d => { if (d.status) setDashboard(d.data); })
      .catch(() => {});

    fetch(`${API_BASE}/suppliers`)
      .then(r => r.json())
      .then(d => { if (d.status) setSuppliers(d.data); })
      .catch(() => {});
  }, []);

  // Fetch AP
  const fetchAp = useCallback(async (filters) => {
    setApLoading(true);
    setApError('');
    try {
      const body = { start_date: filters.start_date, end_date: filters.end_date, page: filters.page, limit: filters.limit };
      if (filters.supp_number) body.supp_number = filters.supp_number;
      const res = await fetch(`${API_BASE}/ap-summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const d = await res.json();
      if (d.status) setApData(d.data);
      else setApError(d.result_desc || 'เกิดข้อผิดพลาด');
    } catch (e) {
      setApError('ไม่สามารถเชื่อมต่อ API ได้');
    } finally {
      setApLoading(false);
    }
  }, []);

  // Fetch PO
  const fetchPo = useCallback(async (filters) => {
    setPoLoading(true);
    setPoError('');
    try {
      const body = { start_date: filters.start_date, end_date: filters.end_date, page: filters.page, limit: filters.limit };
      if (filters.supp_number) body.supp_number = filters.supp_number;
      if (filters.open_close !== '') body.open_close = filters.open_close === '1';
      const res = await fetch(`${API_BASE}/po-summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const d = await res.json();
      if (d.status) setPoData(d.data);
      else setPoError(d.result_desc || 'เกิดข้อผิดพลาด');
    } catch (e) {
      setPoError('ไม่สามารถเชื่อมต่อ API ได้');
    } finally {
      setPoLoading(false);
    }
  }, []);

  // Fetch PettyCash
  const fetchPetty = useCallback(async (filters) => {
    setPettyLoading(true);
    setPettyError('');
    try {
      const body = { start_date: filters.start_date, end_date: filters.end_date, page: filters.page, limit: filters.limit };
      if (filters.employee_code) body.employee_code = filters.employee_code;
      if (filters.dept_code) body.dept_code = filters.dept_code;
      const res = await fetch(`${API_BASE}/petty-cash-summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const d = await res.json();
      if (d.status) setPettyData(d.data);
      else setPettyError(d.result_desc || 'เกิดข้อผิดพลาด');
    } catch (e) {
      setPettyError('ไม่สามารถเชื่อมต่อ API ได้');
    } finally {
      setPettyLoading(false);
    }
  }, []);

  // Fetch Monthly Expenses
  const fetchMonthly = useCallback(async (year) => {
    setMonthlyLoading(true);
    setMonthlyError('');
    try {
      const res = await fetch(`${API_BASE}/monthly-expenses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ year })
      });
      const d = await res.json();
      if (d.status) setMonthlyData(d.data);
      else setMonthlyError(d.result_desc || 'เกิดข้อผิดพลาด');
    } catch (e) {
      setMonthlyError('ไม่สามารถเชื่อมต่อ API ได้');
    } finally {
      setMonthlyLoading(false);
    }
  }, []);

  // Auto-fetch on tab switch
  useEffect(() => {
    if (activeTab === 'ap') fetchAp(apFilters);
    if (activeTab === 'po') fetchPo(poFilters);
    if (activeTab === 'petty') fetchPetty(pettyFilters);
    if (activeTab === 'monthly') fetchMonthly(monthlyYear);
  }, [activeTab]); // eslint-disable-line

  const tabs = [
    { id: 'dashboard', label: 'ภาพรวม', icon: Building2 },
    { id: 'ap', label: 'AP จ่ายเงิน', icon: CreditCard },
    { id: 'po', label: 'ใบสั่งซื้อ PO', icon: ShoppingCart },
    { id: 'petty', label: 'เบิกค่าใช้จ่าย', icon: Wallet },
    { id: 'monthly', label: 'รายจ่ายรายเดือน', icon: BarChart2 }
  ];

  return (
    <div className="p-4 min-h-screen bg-gray-50">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Corp ERP Report</h1>
            <p className="text-sm text-gray-500">PsiCorp HQ — รายงานฝั่งจ่ายเงิน</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-white rounded-xl p-1 shadow-sm border border-gray-200">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-indigo-600 text-white shadow'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div>
          {!dashboard ? (
            <div className="text-center py-12 text-gray-400">
              <RefreshCw className="w-8 h-8 mx-auto animate-spin mb-2" />
              <p>กำลังโหลด...</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-4">
                ข้อมูลเดือนนี้ ({fmtDate(dashboard.period_start)} — {fmtDate(dashboard.period_end)})
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <DashCard
                  icon={CreditCard}
                  color="from-blue-500 to-blue-700"
                  title="AP จ่ายเงิน"
                  count={dashboard.ap_count}
                  amount={dashboard.ap_total}
                  sub={`ชำระแล้ว ฿${fmt(dashboard.ap_paid)}`}
                  warning={dashboard.ap_overdue_count > 0 ? `เกินกำหนด ${dashboard.ap_overdue_count} รายการ` : null}
                />
                <DashCard
                  icon={ShoppingCart}
                  color="from-emerald-500 to-emerald-700"
                  title="ใบสั่งซื้อ PO"
                  count={dashboard.po_count}
                  amount={dashboard.po_total}
                  sub={`รอรับสินค้า ${dashboard.po_open} ใบ`}
                />
                <DashCard
                  icon={Wallet}
                  color="from-amber-500 to-amber-700"
                  title="เบิกค่าใช้จ่าย"
                  count={dashboard.petty_count}
                  amount={dashboard.petty_total}
                  sub=""
                />
              </div>
            </>
          )}
        </div>
      )}

      {/* AP Tab */}
      {activeTab === 'ap' && (
        <div>
          <FilterBar
            filters={apFilters}
            suppliers={suppliers}
            showSupplier
            onChange={setApFilters}
            onSearch={() => fetchAp({ ...apFilters, page: 1 })}
          />
          {apError && <ErrorMsg msg={apError} />}
          {apLoading ? <Loading /> : apData && (
            <>
              <SummaryCards items={[
                { label: 'รายการทั้งหมด', value: apData.summary.total_bills?.toLocaleString() },
                { label: 'ยอดรวม', value: `฿${fmt(apData.summary.total_amount)}` },
                { label: 'ชำระแล้ว', value: `฿${fmt(apData.summary.total_paid)}` },
                { label: 'ค้างชำระ', value: `฿${fmt(apData.summary.total_outstanding)}`, highlight: true }
              ]} />
              <div className="overflow-x-auto rounded-xl shadow-sm border border-gray-200 bg-white">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-indigo-50 text-indigo-800">
                      <th className="px-4 py-3 text-left">เลขที่ใบแจ้งหนี้</th>
                      <th className="px-4 py-3 text-left">วันที่</th>
                      <th className="px-4 py-3 text-left">Supplier</th>
                      <th className="px-4 py-3 text-left">ครบกำหนด</th>
                      <th className="px-4 py-3 text-right">ยอดรวม</th>
                      <th className="px-4 py-3 text-right">ชำระแล้ว</th>
                      <th className="px-4 py-3 text-right">ค้างชำระ</th>
                      <th className="px-4 py-3 text-center">สถานะ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apData.data.map((row, i) => (
                      <tr key={row.APBillNo} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-2 font-mono text-xs">{row.APBillNo}</td>
                        <td className="px-4 py-2">{fmtDate(row.APBillDate)}</td>
                        <td className="px-4 py-2 max-w-xs truncate" title={row.Supp_Name}>{row.Supp_Name || row.Supp_Number}</td>
                        <td className="px-4 py-2">{fmtDate(row.APDueDate)}</td>
                        <td className="px-4 py-2 text-right">{fmt(row.TotalAmount)}</td>
                        <td className="px-4 py-2 text-right text-green-700">{fmt(row.PaidAmount)}</td>
                        <td className="px-4 py-2 text-right text-red-600">{fmt(row.OutstandingAmount)}</td>
                        <td className="px-4 py-2 text-center">
                          <StatusBadge status={row.Status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                pagination={apData.pagination}
                onPage={(p) => { const f = { ...apFilters, page: p }; setApFilters(f); fetchAp(f); }}
              />
            </>
          )}
        </div>
      )}

      {/* PO Tab */}
      {activeTab === 'po' && (
        <div>
          <FilterBar
            filters={poFilters}
            suppliers={suppliers}
            showSupplier
            showOpenClose
            onChange={setPoFilters}
            onSearch={() => fetchPo({ ...poFilters, page: 1 })}
          />
          {poError && <ErrorMsg msg={poError} />}
          {poLoading ? <Loading /> : poData && (
            <>
              <SummaryCards items={[
                { label: 'PO ทั้งหมด', value: poData.summary.total_po?.toLocaleString() },
                { label: 'ยอดรวม', value: `฿${fmt(poData.summary.total_amount)}` },
                { label: 'รอรับสินค้า', value: poData.summary.open_count?.toLocaleString(), highlight: true },
                { label: 'ปิดแล้ว', value: poData.summary.closed_count?.toLocaleString() }
              ]} />
              <div className="overflow-x-auto rounded-xl shadow-sm border border-gray-200 bg-white">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-emerald-50 text-emerald-800">
                      <th className="px-4 py-3 text-left">เลขที่ PO</th>
                      <th className="px-4 py-3 text-left">วันที่สั่ง</th>
                      <th className="px-4 py-3 text-left">Supplier</th>
                      <th className="px-4 py-3 text-left">ผู้สั่งซื้อ</th>
                      <th className="px-4 py-3 text-right">ยอดรวม</th>
                      <th className="px-4 py-3 text-right">จำนวน</th>
                      <th className="px-4 py-3 text-right">รับแล้ว</th>
                      <th className="px-4 py-3 text-center">สถานะ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {poData.data.map((row, i) => (
                      <tr key={row.Order_Number} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-2 font-mono text-xs">{row.Order_Number}</td>
                        <td className="px-4 py-2">{fmtDate(row.Order_date)}</td>
                        <td className="px-4 py-2 max-w-xs truncate" title={row.Supp_Name}>{row.Supp_Name || row.Supp_Number}</td>
                        <td className="px-4 py-2">
                          <div>{row.BuyerName || row.Buyers || '-'}</div>
                          {row.BuyerName && row.Buyers && <div className="text-xs text-gray-400">{row.Buyers}</div>}
                        </td>
                        <td className="px-4 py-2 text-right">{fmt(row.TotalAmount)}</td>
                        <td className="px-4 py-2 text-right">{Number(row.total_qty || 0).toLocaleString()}</td>
                        <td className="px-4 py-2 text-right">{Number(row.received_qty || 0).toLocaleString()}</td>
                        <td className="px-4 py-2 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            row.Open_Close ? 'bg-gray-100 text-gray-600' : 'bg-amber-100 text-amber-700'
                          }`}>
                            {row.Open_Close ? 'ปิดแล้ว' : 'รอรับ'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                pagination={poData.pagination}
                onPage={(p) => { const f = { ...poFilters, page: p }; setPoFilters(f); fetchPo(f); }}
              />
            </>
          )}
        </div>
      )}

      {/* Monthly Expenses Tab */}
      {activeTab === 'monthly' && (
        <div>
          {/* Year selector */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4 flex items-end gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">ปี (ค.ศ.)</label>
              <select
                value={monthlyYear}
                onChange={e => setMonthlyYear(parseInt(e.target.value))}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
              >
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <button
              onClick={() => fetchMonthly(monthlyYear)}
              className="flex items-center gap-2 px-4 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
            >
              <Search className="w-4 h-4" />
              ดูข้อมูล
            </button>
          </div>

          {monthlyError && <ErrorMsg msg={monthlyError} />}
          {monthlyLoading ? <Loading /> : monthlyData && (
            <>
              {/* Main summary table — AP + PettyCash (ไม่รวม PO) */}
              <div className="overflow-x-auto rounded-xl shadow-sm border border-gray-200 bg-white mb-6">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-indigo-50 text-indigo-800">
                      <th className="px-4 py-3 text-left">เดือน</th>
                      <th className="px-4 py-3 text-right">AP จ่ายเงิน (Supplier)</th>
                      <th className="px-4 py-3 text-right">เบิกค่าใช้จ่าย (PI)</th>
                      <th className="px-4 py-3 text-right font-bold">รวมทั้งหมด</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyData.by_month.map((row, i) => (
                      <tr key={row.mo} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-2 font-medium text-gray-700">{row.month_name} {monthlyData.year}</td>
                        <td className="px-4 py-2 text-right text-blue-700">{row.ap_amount > 0 ? fmt(row.ap_amount) : '-'}</td>
                        <td className="px-4 py-2 text-right text-amber-700">{row.petty_amount > 0 ? fmt(row.petty_amount) : '-'}</td>
                        <td className="px-4 py-2 text-right font-bold text-gray-800">{row.total > 0 ? fmt(row.total) : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-indigo-100 font-bold text-indigo-900">
                      <td className="px-4 py-3">รวมทั้งปี</td>
                      <td className="px-4 py-3 text-right">{fmt(monthlyData.grand_total.ap)}</td>
                      <td className="px-4 py-3 text-right">{fmt(monthlyData.grand_total.petty)}</td>
                      <td className="px-4 py-3 text-right">{fmt(monthlyData.grand_total.total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* PettyCash by GExpend category */}
              {monthlyData.categories.length > 0 && (
                <div className="overflow-x-auto rounded-xl shadow-sm border border-gray-200 bg-white">
                  <div className="px-4 py-3 bg-amber-50 border-b border-amber-100">
                    <h3 className="text-sm font-semibold text-amber-800">เบิกค่าใช้จ่าย — แยกตามหมวดค่าใช้จ่าย</h3>
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-amber-50 text-amber-800">
                        <th className="px-4 py-3 text-left">เดือน</th>
                        {monthlyData.categories.map(c => (
                          <th key={c.code} className="px-3 py-3 text-right text-xs" title={c.desc}>{c.desc.length > 12 ? c.desc.slice(0,12)+'…' : c.desc}</th>
                        ))}
                        <th className="px-4 py-3 text-right font-bold">รวม</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyData.by_month.map((row, i) => (
                        <tr key={row.mo} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-4 py-2 font-medium text-gray-700">{row.month_name}</td>
                          {monthlyData.categories.map(c => (
                            <td key={c.code} className="px-3 py-2 text-right text-amber-700">
                              {row.petty_by_cat[c.code] > 0 ? fmt(row.petty_by_cat[c.code]) : '-'}
                            </td>
                          ))}
                          <td className="px-4 py-2 text-right font-medium">{row.petty_amount > 0 ? fmt(row.petty_amount) : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-amber-100 font-bold text-amber-900">
                        <td className="px-4 py-3">รวม</td>
                        {monthlyData.categories.map(c => (
                          <td key={c.code} className="px-3 py-3 text-right">{fmt(monthlyData.grand_total.petty_by_cat[c.code] || 0)}</td>
                        ))}
                        <td className="px-4 py-3 text-right">{fmt(monthlyData.grand_total.petty)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* PettyCash Tab — เฉพาะ PI (จ่ายจริง) + อนุมัติแล้ว */}
      {activeTab === 'petty' && (
        <div>
          <FilterBar
            filters={pettyFilters}
            onChange={setPettyFilters}
            onSearch={() => fetchPetty({ ...pettyFilters, page: 1 })}
            showDeptCode
            showGExpendCode
          />
          {pettyError && <ErrorMsg msg={pettyError} />}
          {pettyLoading ? <Loading /> : pettyData && (
            <>
              <SummaryCards items={[
                { label: 'รายการทั้งหมด', value: pettyData.summary.total_docs?.toLocaleString() },
                { label: 'ยอดรวม (อนุมัติแล้ว)', value: `฿${fmt(pettyData.summary.total_amount)}` }
              ]} />
              <div className="overflow-x-auto rounded-xl shadow-sm border border-gray-200 bg-white">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-amber-50 text-amber-800">
                      <th className="px-4 py-3 text-left">เลขที่เอกสาร</th>
                      <th className="px-4 py-3 text-left">วันที่</th>
                      <th className="px-4 py-3 text-left">หมวดค่าใช้จ่าย</th>
                      <th className="px-4 py-3 text-left">แผนก</th>
                      <th className="px-4 py-3 text-left">รายละเอียด</th>
                      <th className="px-4 py-3 text-right">ยอดรวม</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pettyData.data.map((row, i) => (
                      <tr key={row.VoucherNo} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-2 font-mono text-xs">{row.VoucherNo}</td>
                        <td className="px-4 py-2">{fmtDate(row.DocDate)}</td>
                        <td className="px-4 py-2 text-xs text-amber-800">{row.GExpendDesc || '-'}</td>
                        <td className="px-4 py-2">{row.DeptCode || '-'}</td>
                        <td className="px-4 py-2 max-w-xs truncate text-gray-600" title={row.Remark}>{row.Remark || '-'}</td>
                        <td className="px-4 py-2 text-right font-medium">{fmt(row.TotalAmount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                pagination={pettyData.pagination}
                onPage={(p) => { const f = { ...pettyFilters, page: p }; setPettyFilters(f); fetchPetty(f); }}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ---- Sub-components ----

function DashCard({ icon: Icon, color, title, count, amount, sub, warning }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className={`bg-gradient-to-r ${color} p-4 text-white`}>
        <div className="flex items-center gap-3">
          <Icon className="w-6 h-6" />
          <span className="font-semibold">{title}</span>
        </div>
        <div className="mt-2 text-2xl font-bold">{Number(count || 0).toLocaleString()} รายการ</div>
      </div>
      <div className="p-4">
        <div className="text-lg font-bold text-gray-800">฿{fmt(amount)}</div>
        {sub && <div className="text-sm text-gray-500 mt-1">{sub}</div>}
        {warning && (
          <div className="flex items-center gap-1 mt-2 text-red-600 text-sm">
            <AlertCircle className="w-4 h-4" />
            {warning}
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCards({ items }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
      {items.map((item, i) => (
        <div key={i} className={`rounded-xl p-3 border ${item.highlight ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'}`}>
          <div className="text-xs text-gray-500 mb-1">{item.label}</div>
          <div className={`text-base font-bold ${item.highlight ? 'text-red-700' : 'text-gray-800'}`}>{item.value}</div>
        </div>
      ))}
    </div>
  );
}

function FilterBar({ filters, suppliers = [], showSupplier, showOpenClose, showEmployeeCode, showDeptCode, showGExpendCode, onChange, onSearch }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs text-gray-500 mb-1">จากวันที่</label>
          <MaskedDatePicker
            value={filters.start_date}
            onChange={(str) => onChange({ ...filters, start_date: str })}
            placeholderText="DD/MM/YYYY"
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 w-32"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">ถึงวันที่</label>
          <MaskedDatePicker
            value={filters.end_date}
            onChange={(str) => onChange({ ...filters, end_date: str })}
            placeholderText="DD/MM/YYYY"
            maxDate={new Date()}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 w-32"
          />
        </div>
        {showSupplier && (
          <div>
            <label className="block text-xs text-gray-500 mb-1">Supplier</label>
            <select
              value={filters.supp_number}
              onChange={e => onChange({ ...filters, supp_number: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 max-w-xs"
            >
              <option value="">ทั้งหมด</option>
              {suppliers.map(s => (
                <option key={s.Supp_Number} value={s.Supp_Number}>{s.Supp_Name || s.Supp_Number}</option>
              ))}
            </select>
          </div>
        )}
        {showOpenClose && (
          <div>
            <label className="block text-xs text-gray-500 mb-1">สถานะ PO</label>
            <select
              value={filters.open_close}
              onChange={e => onChange({ ...filters, open_close: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
            >
              <option value="">ทั้งหมด</option>
              <option value="0">รอรับสินค้า</option>
              <option value="1">ปิดแล้ว</option>
            </select>
          </div>
        )}
        {showGExpendCode && (
          <div>
            <label className="block text-xs text-gray-500 mb-1">รหัสหมวดค่าใช้จ่าย</label>
            <input
              type="text"
              value={filters.gexpend_code || ''}
              onChange={e => onChange({ ...filters, gexpend_code: e.target.value })}
              placeholder="เช่น 05"
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 w-24"
            />
          </div>
        )}
        {showDeptCode && (
          <div>
            <label className="block text-xs text-gray-500 mb-1">รหัสแผนก</label>
            <input
              type="text"
              value={filters.dept_code}
              onChange={e => onChange({ ...filters, dept_code: e.target.value })}
              placeholder="เช่น 001"
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 w-24"
            />
          </div>
        )}
        <button
          onClick={onSearch}
          className="flex items-center gap-2 px-4 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
        >
          <Search className="w-4 h-4" />
          ค้นหา
        </button>
      </div>
    </div>
  );
}

function Pagination({ pagination, onPage }) {
  if (!pagination || pagination.total_pages <= 1) return null;
  const { page, total_pages, total, limit } = pagination;
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  return (
    <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
      <span>แสดง {start.toLocaleString()}–{end.toLocaleString()} จาก {total.toLocaleString()} รายการ</span>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPage(1)}
          disabled={page <= 1}
          className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
        >หน้าแรก</button>
        <button
          onClick={() => onPage(page - 1)}
          disabled={page <= 1}
          className="p-1.5 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-40"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="flex gap-1">
          {Array.from({ length: Math.min(5, total_pages) }, (_, i) => {
            let pageNum;
            if (total_pages <= 5) {
              pageNum = i + 1;
            } else {
              if (page <= 3) pageNum = i + 1;
              else if (page >= total_pages - 2) pageNum = total_pages - 4 + i;
              else pageNum = page - 2 + i;
            }
            return (
              <button
                key={pageNum}
                onClick={() => onPage(pageNum)}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  pageNum === page ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {pageNum}
              </button>
            );
          })}
        </div>
        <button
          onClick={() => onPage(page + 1)}
          disabled={page >= total_pages}
          className="p-1.5 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-40"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
        <button
          onClick={() => onPage(total_pages)}
          disabled={page >= total_pages}
          className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
        >หน้าสุดท้าย</button>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    'ชำระแล้ว': 'bg-green-100 text-green-700',
    'เกินกำหนด': 'bg-red-100 text-red-700',
    'รอชำระ': 'bg-yellow-100 text-yellow-700'
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || 'bg-gray-100 text-gray-600'}`}>
      {status}
    </span>
  );
}

function Loading() {
  return (
    <div className="flex items-center justify-center py-16 text-gray-400">
      <RefreshCw className="w-6 h-6 animate-spin mr-2" />
      <span>กำลังโหลดข้อมูล...</span>
    </div>
  );
}

function ErrorMsg({ msg }) {
  return (
    <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-4 mb-4 text-red-700 text-sm">
      <AlertCircle className="w-5 h-5 flex-shrink-0" />
      {msg}
    </div>
  );
}
